import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ticket, Pencil, X } from "lucide-react";
import api, { apiError } from "../lib/api";

const inputCls = "w-full bg-slate-50 border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/40";
const PLAN_LABELS = { student_annual: "Student", pro_annual: "Pro", lifetime: "Lifetime" };

function VoucherForm({ initial, onSave, onClose }) {
  const [f, setF] = useState({ code: "", percent: 25, plans: ["student_annual", "pro_annual", "lifetime"], active: true, internal: false, max_redemptions: "", ...initial });
  const togglePlan = (p) => setF({ ...f, plans: f.plans.includes(p) ? f.plans.filter((x) => x !== p) : [...f.plans, p] });
  const submit = async (e) => {
    e.preventDefault();
    if (!f.plans.length) { toast.error("Select at least one plan"); return; }
    try {
      await onSave({ code: f.code, percent: Number(f.percent), plans: f.plans, active: f.active,
        internal: f.internal, max_redemptions: f.max_redemptions ? Number(f.max_redemptions) : null });
      onClose();
    } catch (e2) { toast.error(apiError(e2)); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl p-6 w-full max-w-md my-8 space-y-4" data-testid="voucher-form-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">{initial?.id ? "Edit voucher" : "New voucher"}</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1"><X size={18} /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">Code</label>
            <input data-testid="voucher-form-code" className={inputCls} value={f.code} onChange={(e) => setF({ ...f, code: e.target.value })} required /></div>
          <div><label className="text-xs font-semibold text-slate-500">Discount %</label>
            <input data-testid="voucher-form-percent" type="number" min={1} max={100} className={inputCls} value={f.percent} onChange={(e) => setF({ ...f, percent: e.target.value })} required /></div>
        </div>
        <div><label className="text-xs font-semibold text-slate-500">Applies to plans</label>
          <div className="flex gap-3 mt-1.5">
            {Object.entries(PLAN_LABELS).map(([k, label]) => (
              <label key={k} className="flex items-center gap-1.5 text-sm font-medium cursor-pointer">
                <input type="checkbox" data-testid={`voucher-form-plan-${k}`} checked={f.plans.includes(k)} onChange={() => togglePlan(k)} className="w-4 h-4 accent-emerald-600" /> {label}
              </label>
            ))}
          </div></div>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="text-xs font-semibold text-slate-500">Max uses (blank = unlimited)</label>
            <input data-testid="voucher-form-max" type="number" min={1} className={inputCls} value={f.max_redemptions || ""} onChange={(e) => setF({ ...f, max_redemptions: e.target.value })} /></div>
          <div className="flex items-end gap-4 pb-2">
            <label className="flex items-center gap-1.5 text-sm font-medium"><input type="checkbox" data-testid="voucher-form-active" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} className="w-4 h-4 accent-emerald-600" /> Active</label>
            <label className="flex items-center gap-1.5 text-sm font-medium" title="Internal codes are for admin allocation only — never promoted"><input type="checkbox" data-testid="voucher-form-internal" checked={f.internal} onChange={(e) => setF({ ...f, internal: e.target.checked })} className="w-4 h-4 accent-amber-500" /> Internal</label>
          </div>
        </div>
        <button data-testid="voucher-form-save-btn" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-xl transition-all">Save voucher</button>
      </form>
    </div>
  );
}

export function AdminVouchers() {
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/admin/vouchers").then((r) => setRows(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (editing?.id) await api.put(`/admin/vouchers/${editing.id}`, payload);
    else await api.post("/admin/vouchers", payload);
    toast.success("Saved");
    load();
  };

  const toggleActive = async (v) => {
    await api.put(`/admin/vouchers/${v.id}`, { code: v.code, percent: v.percent, plans: v.plans,
      active: !v.active, internal: v.internal, max_redemptions: v.max_redemptions });
    load();
  };

  return (
    <div className="max-w-3xl">
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-slate-400">Members enter a code when registering — the discount applies automatically at checkout (first payment only).</p>
        <button data-testid="admin-add-voucher-btn" onClick={() => setEditing({})}
          className="bg-slate-900 text-white text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-slate-800 transition-all shrink-0 ml-4 flex items-center gap-1.5"><Ticket size={15} /> New voucher</button>
      </div>
      <div className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100" data-testid="vouchers-list">
        {rows.map((v) => (
          <div key={v.id} className="flex items-center justify-between gap-3 px-5 py-3.5">
            <div className="min-w-0">
              <p className="font-semibold text-sm flex items-center gap-2">
                <span className={`cmd-chip ${v.active ? "text-slate-900" : "text-slate-400 line-through"}`}>{v.code}</span>
                <span className="text-emerald-700 font-bold">{v.percent}% off</span>
                {v.internal && <span className="bg-amber-50 text-amber-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Internal only</span>}
              </p>
              <p className="text-xs text-slate-400 mt-0.5">
                {v.plans.map((p) => PLAN_LABELS[p]).join(", ")} · used {v.redemptions}{v.max_redemptions ? ` / ${v.max_redemptions}` : ""} times
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`voucher-toggle-${v.code}`} onClick={() => toggleActive(v)}
                className={`text-xs font-bold px-2.5 py-1 rounded-full ${v.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
                {v.active ? "Active" : "Inactive"}
              </button>
              <button data-testid={`voucher-edit-${v.code}`} onClick={() => setEditing(v)} className="text-slate-400 hover:text-emerald-600 p-1.5"><Pencil size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      {editing !== null && <VoucherForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
    </div>
  );
}
